<template>
    <v-card outlined class="test-dte-sender" color="white">
        <v-card-title class="pb-2">
            Envio masivo de comprobantes de prueba
        </v-card-title>

        <v-card-text>
            <div class="text-body-2 mb-4">
                Selecciona los comprobantes que deseas probar, indica la cantidad para cada tipo y luego presiona "Enviar pruebas". El sistema procesara cada envio usando el flujo actual de facturacion y mostrara el resultado detallado para que puedas reintentar solo los comprobantes necesarios.
            </div>

            <v-alert
                v-if="errorMessages.length"
                dense
                outlined
                type="error"
                class="mb-4"
            >
                <div
                    v-for="(message, index) in errorMessages"
                    :key="`error-${index}`"
                >
                    {{ message }}
                </div>
            </v-alert>

            <v-row no-gutters>
                <v-col
                    v-for="document in documents"
                    :key="document.type"
                    cols="12"
                    md="6"
                    lg="4"
                    class="pa-2"
                >
                    <v-card outlined class="fill-height">
                        <v-card-text>
                            <v-checkbox
                                v-model="document.enabled"
                                :disabled="running"
                                :label="document.label"
                                hide-details
                                class="mt-0 pt-0"
                            ></v-checkbox>

                            <v-text-field
                                v-model.number="document.quantity"
                                :disabled="running || !document.enabled"
                                :min="1"
                                label="Cantidad"
                                type="number"
                                hide-details="auto"
                                class="mt-2"
                                @blur="normalizeQuantity(document)"
                            ></v-text-field>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <div class="d-flex flex-wrap align-center mt-4">
                <v-btn
                    color="primary"
                    :disabled="running"
                    :loading="running"
                    @click="sendTests"
                >
                    Enviar pruebas
                </v-btn>

                <v-btn
                    class="ml-2"
                    outlined
                    :disabled="running"
                    @click="resetForm"
                >
                    Limpiar
                </v-btn>

                <div class="text-body-2 ml-4 mt-2 mt-sm-0">
                    Total solicitado: <strong>{{ totalRequested }}</strong>
                </div>
            </div>

            <div class="mt-4">
                <div class="text-body-2 mb-2">
                    {{ running ? processingLabel : "Progreso" }}
                </div>
                <div class="text-caption mb-2 grey--text text--darken-1">
                    El envio puede tardar varios minutos si ejecutas muchas pruebas. Puedes dejar esta pantalla abierta mientras se completan los comprobantes.
                </div>
                <v-progress-linear
                    :active="running || hasSummary"
                    :indeterminate="running && !summary"
                    :value="progressValue"
                    color="primary"
                    height="12"
                    rounded
                ></v-progress-linear>
            </div>

            <v-row v-if="hasSummary" class="mt-4" no-gutters>
                <v-col cols="12" sm="6" lg="3" class="pa-2">
                    <v-card outlined>
                        <v-card-text>
                            <div class="text-caption">Total solicitado</div>
                            <div class="text-h6">{{ summary.totalRequested }}</div>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12" sm="6" lg="3" class="pa-2">
                    <v-card outlined>
                        <v-card-text>
                            <div class="text-caption">Total procesado</div>
                            <div class="text-h6">{{ summary.processed }}</div>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12" sm="6" lg="3" class="pa-2">
                    <v-card outlined>
                        <v-card-text>
                            <div class="text-caption">Total exitoso</div>
                            <div class="text-h6 success--text">{{ summary.successCount }}</div>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12" sm="6" lg="3" class="pa-2">
                    <v-card outlined>
                        <v-card-text>
                            <div class="text-caption">Total fallido</div>
                            <div class="text-h6 error--text">{{ summary.errorCount }}</div>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-row v-if="hasContingencySummary" no-gutters>
                <v-col cols="12" sm="6" lg="3" class="pa-2">
                    <v-card outlined>
                        <v-card-text>
                            <div class="text-caption">Meta contingencia</div>
                            <div class="text-h6">{{ contingencySummary.requestedCount }}</div>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12" sm="6" lg="3" class="pa-2">
                    <v-card outlined>
                        <v-card-text>
                            <div class="text-caption">Ya existentes</div>
                            <div class="text-h6">{{ contingencySummary.existingCount }}</div>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12" sm="6" lg="3" class="pa-2">
                    <v-card outlined>
                        <v-card-text>
                            <div class="text-caption">Creados ahora</div>
                            <div class="text-h6 success--text">{{ contingencySummary.createdCount }}</div>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12" sm="6" lg="3" class="pa-2">
                    <v-card outlined>
                        <v-card-text>
                            <div class="text-caption">Pendientes listos</div>
                            <div class="text-h6">{{ contingencySummary.currentPendingCount }}</div>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-simple-table v-if="results.length > 0" class="mt-4">
                <template v-slot:default>
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Tipo</th>
                            <th>Estado</th>
                            <th>Mensaje</th>
                            <th>Referencia</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="item in results"
                            :key="`${item.type}-${item.index}-${item.reference || 'none'}`"
                        >
                            <td>{{ item.index }}</td>
                            <td>{{ item.label || item.type }}</td>
                            <td>
                                <v-chip
                                    x-small
                                    dark
                                    :color="getStatusColor(item.status)"
                                >
                                    {{ item.status }}
                                </v-chip>
                            </td>
                            <td>{{ item.message }}</td>
                            <td>{{ item.reference || "-" }}</td>
                        </tr>
                    </tbody>
                </template>
            </v-simple-table>

            <v-simple-table v-if="contingencySteps.length > 0" class="mt-4">
                <template v-slot:default>
                    <thead>
                        <tr>
                            <th>Paso</th>
                            <th>Estado</th>
                            <th>Detalle</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(step, index) in contingencySteps"
                            :key="`contingency-step-${index}`"
                        >
                            <td>{{ step.step }}</td>
                            <td>
                                <v-chip
                                    x-small
                                    dark
                                    :color="getContingencyStepColor(step.status)"
                                >
                                    {{ step.status }}
                                </v-chip>
                            </td>
                            <td>{{ step.message }}</td>
                        </tr>
                    </tbody>
                </template>
            </v-simple-table>
        </v-card-text>
    </v-card>
</template>

<script>
const DEFAULT_DOCUMENTS = () => ([
    { type: "FCF", label: "Consumidor Final", enabled: false, quantity: 90 },
    { type: "CCF", label: "Credito Fiscal", enabled: false, quantity: 75 },
    { type: "FEX", label: "Factura de Exportacion", enabled: false, quantity: 90 },
    { type: "NC", label: "Nota de Credito", enabled: false, quantity: 50 },
    { type: "ND", label: "Nota de Debito", enabled: false, quantity: 50 },
    { type: "SE", label: "Sujeto Excluido", enabled: false, quantity: 25 },
    { type: "CONTINGENCY", label: "Contingencia", enabled: false, quantity: 5 },
    { type: "INVALIDATION", label: "Invalidaciones", enabled: false, quantity: 5 },
]);

export default {
    name: "TestDteSender",
    props: {
        business: {
            type: Object,
            default: () => null,
        },
        branch: {
            type: Object,
            default: () => null,
        },
    },
    mounted() {
        this.restorePersistedJob();
    },
    watch: {
        branch: {
            deep: true,
            handler(newValue, oldValue) {
                if (!oldValue || String(newValue?.ID_Branch || "") === String(oldValue?.ID_Branch || "")) {
                    return;
                }

                this.resetForm();
                this.restorePersistedJob();
            },
        },
    },
    data() {
        return {
            documents: DEFAULT_DOCUMENTS(),
            running: false,
            currentJobId: null,
            pollingTimer: null,
            results: [],
            summary: null,
            contingencySummary: null,
            contingencySteps: [],
            contingencyRunning: false,
            contingencyFlowJobId: null,
            failedCountsAppliedJobId: null,
            errorMessages: [],
            user: this.getUserFromStorage(),
        };
    },
    computed: {
        currentBusinessId() {
            return this.business?.ID_Business || this.user?.businessid || this.user?.ID_Business || "";
        },
        currentBranchId() {
            return this.branch?.ID_Branch || this.branch?.id_branch || this.user?.branch || this.user?.branchid || this.user?.ID_Branch || "";
        },
        currentUserId() {
            return this.user?.id || this.user?.ID_User || this.user?.user || "";
        },
        currentTypeData() {
            const branch = this.branch?.AuthFeMode != null && this.branch?.AuthFeMode !== ""
                ? this.branch
                : this.getBranchFromStorage();
            return Number(branch?.AuthFeMode) === 1 ? "01" : "00";
        },
        currentPointSale() {
            const branch = this.branch?.codPointSale != null || this.branch?.codpuntoventa != null || this.branch?.CodPuntoVenta != null || this.branch?.CodPointSale != null
                ? this.branch
                : this.getBranchFromStorage();

            return (
                branch?.codPointSale ||
                branch?.codpuntoventa ||
                branch?.CodPuntoVenta ||
                branch?.CodPointSale ||
                localStorage.getItem("codPointSale") ||
                null
            );
        },
        totalRequested() {
            return this.documents.reduce((total, document) => {
                if (!document.enabled) {
                    return total;
                }

                const quantity = Number(document.quantity);
                return total + (Number.isFinite(quantity) && quantity > 0 ? quantity : 0);
            }, 0);
        },
        hasSummary() {
            return Boolean(this.summary);
        },
        hasContingencySummary() {
            return Boolean(this.contingencySummary?.requestedCount);
        },
        progressValue() {
            if (!this.summary || !this.summary.totalRequested) {
                return 0;
            }

            return Math.round((this.summary.processed / this.summary.totalRequested) * 100);
        },
        processingLabel() {
            if (this.contingencyRunning) {
                return "Ejecutando contingencia y consulta de lotes...";
            }

            if (!this.summary?.totalRequested) {
                return "Preparando envio...";
            }

            return `Procesando envio... ${this.summary.processed} de ${this.summary.totalRequested}`;
        },
        requestPayload() {
            return {
                ID_Business: this.currentBusinessId,
                ID_Branch: this.currentBranchId,
                user: this.currentUserId,
                codPointSale: this.currentPointSale,
                documents: this.documents.map((document) => ({
                    type: document.type,
                    enabled: document.enabled,
                    quantity: Number(document.quantity),
                })),
            };
        },
    },
    beforeDestroy() {
        this.clearPolling();
    },
    methods: {
        getUserFromStorage() {
            try {
                return JSON.parse(localStorage.getItem("user")) || {};
            } catch (error) {
                return {};
            }
        },
        getBranchFromStorage() {
            try {
                return JSON.parse(localStorage.getItem("branch")) || {};
            } catch (error) {
                return {};
            }
        },
        getActiveJobStorageKey(businessId = this.currentBusinessId) {
            if (!businessId) {
                return null;
            }

            return `test-dte-active-job-${businessId}-${this.currentBranchId || "no-branch"}`;
        },
        persistActiveJob(job = {}) {
            const businessId = job?.ID_Business || this.currentBusinessId;
            const storageKey = this.getActiveJobStorageKey(businessId);

            if (!storageKey || !job?.jobId) {
                return;
            }

            localStorage.setItem(storageKey, JSON.stringify({
                jobId: job.jobId,
                ID_Business: businessId,
                ID_Branch: job?.ID_Branch || this.currentBranchId || null,
                updatedAt: job?.updatedAt || new Date().toISOString(),
            }));
        },
        clearPersistedJob(businessId = this.currentBusinessId) {
            const storageKey = this.getActiveJobStorageKey(businessId);

            if (!storageKey) {
                return;
            }

            localStorage.removeItem(storageKey);
        },
        getPersistedJob() {
            const storageKey = this.getActiveJobStorageKey();

            if (!storageKey) {
                return null;
            }

            try {
                const persistedJob = JSON.parse(localStorage.getItem(storageKey));
                return persistedJob?.jobId ? persistedJob : null;
            } catch (error) {
                this.clearPersistedJob();
                return null;
            }
        },
        async restorePersistedJob() {
            const persistedJob = this.getPersistedJob();

            if (!persistedJob?.jobId) {
                return;
            }

            this.currentJobId = persistedJob.jobId;

            try {
                const response = await this.$API.invoices.getTestDteBatchStatus(persistedJob.jobId);
                this.applyJobState(response);

                if (response.status === "COMPLETED") {
                    this.applyFailedCountsToDocuments(response);
                    if (this.shouldRunContingencyFlow(response)) {
                        await this.runContingencyFlow(response);
                    }
                    this.running = false;
                    return;
                }

                if (response.status === "FAILED") {
                    this.applyFailedCountsToDocuments(response);
                    this.errorMessages = this.normalizeError(response);
                    this.running = false;
                    return;
                }

                this.running = true;
                this.scheduleNextPoll();
            } catch (error) {
                this.currentJobId = null;
                this.running = false;
                this.clearPolling();

                if (this.isMissingJobError(error)) {
                    this.clearPersistedJob();
                }
            }
        },
        normalizeQuantity(document) {
            const quantity = Number(document.quantity);

            if (!Number.isInteger(quantity) || quantity < 1) {
                document.quantity = 1;
            }
        },
        getStatusColor(status) {
            if (status === "PROCESADO" || status === "INVALIDADO") {
                return "success";
            }

            if (status === "CONTINGENCIA") {
                return "success";
            }

            if (status === "YA_EN_CONTINGENCIA") {
                return "success";
            }

            if (status === "DEPENDENCIA_FALTANTE") {
                return "orange darken-2";
            }

            return "error";
        },
        getContingencyStepColor(status) {
            if (status === "COMPLETADO" || status === "RECIBIDO") {
                return "success";
            }

            if (status === "PENDIENTE" || status === "SIN_LOTES") {
                return "warning";
            }

            return "error";
        },
        validateForm() {
            const errors = [];
            const selectedDocuments = this.documents.filter((document) => document.enabled);

            if (!this.currentBusinessId) {
                errors.push("No se encontro el negocio actual.");
            }

            if (!this.currentBranchId) {
                errors.push("No se encontro la sucursal actual.");
            }

            if (selectedDocuments.length === 0) {
                errors.push("Debe seleccionar al menos un tipo de comprobante.");
            }

            selectedDocuments.forEach((document) => {
                const quantity = Number(document.quantity);

                if (!Number.isInteger(quantity) || quantity < 1) {
                    errors.push(`La cantidad de ${document.label} debe ser un entero mayor o igual a 1.`);
                }
            });

            const needsCcf = selectedDocuments.some((document) => document.type === "NC" || document.type === "ND");
            const hasCcf = selectedDocuments.some((document) => document.type === "CCF");

            if (needsCcf && !hasCcf) {
                errors.push("NC y ND requieren incluir al menos un CCF en la misma ejecucion.");
            }

            return errors;
        },
        normalizeError(error) {
            if (typeof error === "string" && error) {
                return [error];
            }

            if (Array.isArray(error?.errors) && error.errors.length > 0) {
                return error.errors;
            }

            if (typeof error?.message === "string" && error.message) {
                return [error.message];
            }

            if (typeof error?.data?.message === "string" && error.data.message) {
                return [error.data.message];
            }

            return ["Ocurrio un error al enviar las pruebas."];
        },
        isMissingJobError(error) {
            const message = [
                error?.message,
                error?.data?.message,
                error?.response?.data?.message,
            ].find((item) => typeof item === "string" && item);

            return error?.status === 404
                || error?.response?.status === 404
                || message === "No se encontró el proceso solicitado o ya expiró."
                || message === "No se encontro el proceso solicitado o ya expiro.";
        },
        clearPolling() {
            if (this.pollingTimer) {
                clearTimeout(this.pollingTimer);
                this.pollingTimer = null;
            }
        },
        applyJobState(job = {}) {
            this.currentJobId = job.jobId || this.currentJobId;
            this.results = Array.isArray(job.results) ? job.results : [];
            this.summary = job.summary || this.summary || {
                totalRequested: this.totalRequested,
                processed: 0,
                successCount: 0,
                errorCount: 0,
            };
            this.contingencySummary = job.contingency || this.contingencySummary;

            if (this.currentJobId) {
                this.persistActiveJob(job);
            }
        },
        resetContingencyState() {
            this.contingencySummary = null;
            this.contingencySteps = [];
            this.contingencyRunning = false;
            this.contingencyFlowJobId = null;
        },
        applyFailedCountsToDocuments(job = {}) {
            if (!job?.jobId || this.failedCountsAppliedJobId === job.jobId || !Array.isArray(job.results)) {
                return;
            }

            const failedCounts = job.results.reduce((accumulator, item) => {
                if (item?.success) {
                    return accumulator;
                }

                const type = String(item?.type || "").trim().toUpperCase();

                if (!type) {
                    return accumulator;
                }

                accumulator[type] = (accumulator[type] || 0) + 1;
                return accumulator;
            }, {});

            if (Object.keys(failedCounts).length === 0) {
                this.failedCountsAppliedJobId = job.jobId;
                return;
            }

            this.documents = this.documents.map((document) => ({
                ...document,
                quantity: failedCounts[document.type] || document.quantity,
            }));

            this.failedCountsAppliedJobId = job.jobId;
        },
        resetForm() {
            this.clearPolling();
            this.documents = DEFAULT_DOCUMENTS();
            this.running = false;
            this.clearPersistedJob();
            this.currentJobId = null;
            this.results = [];
            this.summary = null;
            this.errorMessages = [];
            this.failedCountsAppliedJobId = null;
            this.resetContingencyState();
        },
        buildContingencyFilter() {
            return {
                codPointSale: this.currentPointSale,
                ID_Business: this.currentBusinessId,
                ID_Branch: this.currentBranchId,
                user: this.currentUserId,
                typeData: this.currentTypeData,
                showAllRecords: false,
            };
        },
        addContingencyStep(step) {
            this.contingencySteps = [...this.contingencySteps, step];
        },
        buildContingencyProcessMessage(response = {}) {
            const messages = [];
            const events = Array.isArray(response?.eventosContingencia) ? response.eventosContingencia : [];

            if (events.length > 0) {
                const receivedCount = events.filter((item) => item?.estado === "RECIBIDO").length;
                const errorCount = events.length - receivedCount;
                messages.push(`Eventos enviados: ${events.length}. Recibidos: ${receivedCount}. Fallidos: ${errorCount}.`);
            }

            if (response?.eventoContingencia) {
                messages.push(`Estado evento: ${response.eventoContingencia.estado || "SIN_ESTADO"}`);

                if (response.eventoContingencia.mensaje || response.eventoContingencia.descripcionMsg) {
                    messages.push(response.eventoContingencia.mensaje || response.eventoContingencia.descripcionMsg);
                }

                if (Array.isArray(response.eventoContingencia.observaciones) && response.eventoContingencia.observaciones.length > 0) {
                    messages.push(response.eventoContingencia.observaciones.join(" | "));
                }
            } else {
                messages.push("No se genero evento de contingencia.");
            }

            if (Array.isArray(response?.respuestasRecepcionLotes) && response.respuestasRecepcionLotes.length > 0) {
                messages.push(`Lotes recibidos: ${response.respuestasRecepcionLotes.length}`);
            } else {
                messages.push("No se genero ningun lote.");
            }

            return messages.join(" ");
        },
        buildConsultStep(step, response = {}) {
            if (response?.status === 1) {
                return {
                    step,
                    status: "COMPLETADO",
                    message: "Se consultaron los lotes y se actualizaron los comprobantes.",
                };
            }

            if (response?.status === -1) {
                return {
                    step,
                    status: "PENDIENTE",
                    message: "Hacienda aun no ha devuelto respuesta para el lote de contingencia.",
                };
            }

            return {
                step,
                status: "SIN_LOTES",
                message: "No se encontraron lotes recientes para consultar.",
            };
        },
        shouldRunContingencyFlow(job = {}) {
            return Boolean(
                job?.status === "COMPLETED"
                && job?.jobId
                && job.jobId !== this.contingencyFlowJobId
                && job?.contingency?.requestedCount > 0
            );
        },
        async runContingencyFlow(job = {}) {
            this.contingencyFlowJobId = job.jobId;

            if (!job?.contingency?.currentPendingCount) {
                this.addContingencyStep({
                    step: "Proceso de contingencia",
                    status: "SIN_LOTES",
                    message: "No quedaron comprobantes pendientes en contingencia para enviar.",
                });
                return;
            }

            this.contingencyRunning = true;
            const filterWithLimit = this.buildContingencyFilter();

            try {
                const contingencyResponse = await this.$API.invoices.processSeparatedTestDteContingency(filterWithLimit);
                const contingencyEvents = Array.isArray(contingencyResponse?.eventosContingencia)
                    ? contingencyResponse.eventosContingencia
                    : [];
                const contingencyStatus = contingencyEvents.length > 0
                    ? (contingencyEvents.every((item) => item?.estado === "RECIBIDO")
                        ? "RECIBIDO"
                        : (contingencyEvents.some((item) => item?.estado === "RECIBIDO") ? "PENDIENTE" : "ERROR"))
                    : (contingencyResponse?.eventoContingencia?.estado
                        || (Array.isArray(contingencyResponse?.respuestasRecepcionLotes) && contingencyResponse.respuestasRecepcionLotes.length > 0
                            ? "RECIBIDO"
                            : "SIN_LOTES"));

                this.addContingencyStep({
                    step: "Paso 1: Ejecutar contingencia por comprobante",
                    status: contingencyStatus,
                    message: this.buildContingencyProcessMessage(contingencyResponse),
                });

                const consultResponse = await this.$API.invoices.consultBatchProcess(filterWithLimit);
                this.addContingencyStep(this.buildConsultStep("Paso 2 (a): Consultar lotes", consultResponse));

                if (consultResponse?.status !== 1) {
                    const consultResponse2 = await this.$API.invoices.consultBatchProcess2(filterWithLimit);
                    this.addContingencyStep(this.buildConsultStep("Paso 2 (b): Consultar lotes alternativo", consultResponse2));
                }
            } catch (error) {
                const messages = this.normalizeError(error);
                this.errorMessages = [...this.errorMessages, ...messages];
                this.addContingencyStep({
                    step: "Proceso de contingencia",
                    status: "ERROR",
                    message: messages.join(" "),
                });
            } finally {
                this.contingencyRunning = false;
            }
        },
        scheduleNextPoll() {
            this.clearPolling();
            this.pollingTimer = setTimeout(() => {
                this.pollBatchStatus();
            }, 5000);
        },
        async pollBatchStatus() {
            if (!this.currentJobId) {
                this.running = false;
                return;
            }

            try {
                const response = await this.$API.invoices.getTestDteBatchStatus(this.currentJobId);
                this.applyJobState(response);

                if (response.status === "COMPLETED") {
                    this.clearPolling();
                    this.applyFailedCountsToDocuments(response);
                    if (this.shouldRunContingencyFlow(response)) {
                        await this.runContingencyFlow(response);
                    }
                    this.running = false;
                    return;
                }

                if (response.status === "FAILED") {
                    this.running = false;
                    this.clearPolling();
                    this.applyFailedCountsToDocuments(response);
                    this.errorMessages = this.normalizeError(response);
                    return;
                }

                this.scheduleNextPoll();
            } catch (error) {
                this.running = false;
                this.clearPolling();
                this.errorMessages = this.normalizeError(error);
            }
        },
        async sendTests() {
            if (this.running) {
                return;
            }

            this.errorMessages = this.validateForm();

            if (this.errorMessages.length > 0) {
                return;
            }

            this.running = true;
            this.currentJobId = null;
            this.clearPolling();
            this.clearPersistedJob();
            this.results = [];
            this.summary = null;
            this.failedCountsAppliedJobId = null;
            this.resetContingencyState();
            this.errorMessages = [];

            try {
                const response = await this.$API.invoices.sendTestDteBatch(this.requestPayload);

                if (!response?.success) {
                    this.errorMessages = this.normalizeError(response);
                    this.running = false;
                    return;
                }

                this.applyJobState(response);

                if (!response.jobId) {
                    this.running = false;
                    return;
                }

                this.scheduleNextPoll();
            } catch (error) {
                this.errorMessages = this.normalizeError(error);
                this.running = false;
            } finally {
                if (!this.currentJobId) {
                    this.running = false;
                }
            }
        },
    },
};
</script>

<style scoped>
.test-dte-sender {
    background-color: #fff;
    border: 1px solid rgba(25, 118, 210, 0.18);
}
</style>
